<script lang="ts">
  import dayjs from 'dayjs'
  import { onMount } from 'svelte'
  
  // Props interface
  interface FeedItem {
    link: string
    date: string
    title: string
  }

  // Component state
  let feedItems: FeedItem[] = []
  let isLoading = true
  let error: string | null = null

  let controller: AbortController;

  onMount(async () => {
    try {
      controller = new AbortController();
      const res = await fetch('/api/proxy?type=news', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!res.ok) throw new Error('Failed to fetch news');
      const items = await res.json();
      feedItems = items.slice(0, 6);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch news:', error);
        error = 'お知らせの読み込みに失敗しました';
      }
    } finally {
      isLoading = false;
    }

    return () => {
      if (controller) {
        controller.abort();
      }
    };
  });

  const format = (date: string): string => {
    return dayjs(date).format('YYYY.MM.DD')
  }

  // Keyboard event handler for accessibility
  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const target = event.target as HTMLElement
      const link = target.closest('a')
      if (link) {
        link.click()
      }
    }
  }
</script>

<article>
  <section class="section-title" id="info">
    <h2>お知らせ</h2>
  </section>
  <section class="news-area content-inner">
    {#if isLoading}
      <p class="loading">読み込み中...</p>
    {:else if error}
      <p class="error">{error}</p>
    {:else if feedItems.length === 0}
      <p>現在お知らせはありません。</p>
    {:else}
      <ul class="news-list">
        {#each feedItems as item (item.link)}
          <li>
            <a 
              href={item.link}
              on:keydown={handleKeyDown}
            >
              <span>{format(item.date)}</span>
              {item.title}
            </a>
          </li>
        {/each}
      </ul>

      <p class="item-center">
        <a
          href="https://prtimes.jp/main/html/searchrlp/company_id/43915"
          class="link__bullet-btn"
          on:keydown={handleKeyDown}
        >
          <span>お知らせ一覧</span>
        </a>
      </p>
    {/if}
  </section>
</article>

<style>
  /* Component-specific styles */
  :global(.news-list) {
    display: flex;
    flex-direction: column;
    list-style-type: none;
    margin: 0 auto;
    padding: 0;
    width: 95%;
  }

  :global(.news-list li) {
    margin-top: 30px;
    padding-bottom: 15px;
    border-bottom: 1px solid var(--color-border);
    font-style: normal;
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 30px;
    text-align: justify;
    color: #000000;
  }

  :global(.news-list li a) {
    color: var(--color-main);
    transition: opacity 0.3s;
  }

  :global(.news-list li a:hover) {
    opacity: 0.7;
  }

  :global(.news-list li span) {
    display: inline-block;
    width: 200px;
  }

  .loading,
  .error {
    text-align: center;
    padding: 20px;
    font-size: 1.5rem;
    color: var(--color-main);
  }

  .error {
    color: #dc3545;
  }

  @media screen and (max-width: 768px) {
    :global(.news-list li span) {
      display: block;
    }

    .loading,
    .error {
      font-size: 1.3rem;
      padding: 15px;
    }
  }

  :global(.link__bullet-btn) {
    transition: all 0.3s;
  }

  :global(.link__bullet-btn:hover) {
    background: #7521da;
    color: #fff;
  }

  .news-empty {
    text-align: center;
    padding: 40px 0;
    font-size: 1.2rem;
    color: #666;
  }
</style>
